import { computed, inject, signal, Service } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AuthSessionStore } from '@core/auth/application/auth-session.store';
import { LoggerService, ScopedLogger } from '@core/logging/logger.service';
import { PlatformService } from '@core/platform/platform.service';
import { APP_PATHS } from '@core/routing';
import { StorageKeys } from '@core/storage/storage.keys';
import { StorageService } from '@core/storage/storage.service';
import { ActionState } from '@shared/state/async-action';
import {
  CardUpdateCheckout,
  ChargeVerification,
  CheckoutOutcome,
  CheckoutPurpose,
  INITIAL_SUBSCRIPTION,
  isCheckoutFresh,
  isPremiumTier,
  PaymentMethodKind,
  PendingCheckout,
  planForCycle,
  SubscriptionCard,
  SubscriptionCycle,
  SubscriptionInvoice,
  SubscriptionPlan,
  SubscriptionPolicy,
  SubscriptionQuote,
  SubscriptionSnapshot,
  SubscriptionTier,
} from '../domain/models/subscription.model';
import { CheckoutRequest, SubscriptionRepository } from '../domain/interfaces/subscription.interface';
import { SubscriptionErrorKeys, SubscriptionOps } from '../domain/subscription.errors';
import { mergeSnapshot } from '../infrastructure/mappers/subscription.mapper';

/** Options for a paid checkout (`/subscribe` or `/reactivate`). */
export interface CheckoutOptions {
  readonly cycle?: SubscriptionCycle;
  /** Explicit plan; resolved from the catalog by `cycle` when omitted. */
  readonly planId?: number;
  readonly promoCode?: string | null;
  readonly useWallet?: boolean;
  readonly cardId?: string | null;
  readonly paymentMethod?: PaymentMethodKind | null;
}

/** How a checkout the user returned from settled, and which kind of hand-off it was. */
export interface CheckoutResolution {
  readonly purpose: CheckoutPurpose;
  /** `pending` — not visible yet (the webhook is slower than the redirect); keep listening. */
  readonly status: 'confirmed' | 'pending' | 'failed';
}

/**
 * SubscriptionStore — the SINGLE source of truth for "what is this user's
 * subscription?", ROOT-scoped so every surface reads the same signals.
 *
 * Why root and not the route-scoped facade: subscription state gates UI far
 * outside `/v2/subscription` (the Profile tier row, premium badges, Khoyek's
 * daily cap). Before this store each of those re-read `GET /api/v3/subscription`
 * on its own and could disagree; now they all read these signals and a change
 * made anywhere — start trial, cancel, checkout return, a 429 in the chat —
 * propagates everywhere on the next tick with no cross-feature wiring.
 *
 * Honesty rules (deliberate, and the reason several older fallbacks are gone):
 *  · READS may fall back to the local cache, so a cold/offline start still
 *    paints the last known tier instead of dead-ending.
 *  · MUTATIONS never fall back. A failed trial/cancel/checkout surfaces the real
 *    error — the store NEVER writes a premium tier the server didn't grant.
 *    (`GET /subscription` stays authoritative for every screen.)
 *  · A hosted checkout is asynchronous: handing the tab to the gateway records a
 *    {@link PendingCheckout} marker; the return trip re-reads the server rather
 *    than assuming success.
 *
 * SSR-safe: storage is guarded by {@link StorageService}, `Date.now()` is only
 * read off the render path, and nothing touches `window` directly.
 */
@Service()
export class SubscriptionStore {
  private readonly repo = inject(SubscriptionRepository);
  private readonly storage = inject(StorageService);
  private readonly platform = inject(PlatformService);
  private readonly session = inject(AuthSessionStore);
  private readonly logger: ScopedLogger = inject(LoggerService).forSource('SubscriptionStore');

  // ─── canonical state ──────────────────────────────────────────────────
  private readonly _snapshot = signal<SubscriptionSnapshot>({ ...INITIAL_SUBSCRIPTION });
  private readonly _loaded = signal(false);
  private readonly _plans = signal<readonly SubscriptionPlan[]>([]);
  private readonly _freePolicy = signal<SubscriptionPolicy | null>(null);
  private readonly _invoices = signal<readonly SubscriptionInvoice[]>([]);
  private readonly _cards = signal<readonly SubscriptionCard[]>([]);
  private readonly _quote = signal<SubscriptionQuote | null>(null);
  private readonly _toolsCount = signal<number | null>(null);
  private readonly _pendingCheckout = signal<PendingCheckout | null>(null);

  /** The authoritative subscription snapshot. */
  readonly snapshot = this._snapshot.asReadonly();
  /** True once a snapshot read has SUCCEEDED at least once this session. */
  readonly loaded = this._loaded.asReadonly();
  /** Subscribable catalog from `GET /plans` (empty until loaded). */
  readonly plans = this._plans.asReadonly();
  /** The FREE tier policy from `GET /plans` (`free_policy`); null until the API sends it (GAP 01). */
  readonly catalogFreePolicy = this._freePolicy.asReadonly();
  readonly invoices = this._invoices.asReadonly();
  readonly cards = this._cards.asReadonly();
  /** Khoyek tool count from `GET /tools` (null until read, or when unavailable). */
  readonly toolsCount = this._toolsCount.asReadonly();
  /** Latest `POST /quote` breakdown, or null when none was requested/available. */
  readonly quote = this._quote.asReadonly();
  /** A checkout handed to the gateway and not yet confirmed. */
  readonly pendingCheckout = this._pendingCheckout.asReadonly();

  readonly tier = computed<SubscriptionTier>(() => this._snapshot().tier);
  readonly isPremium = computed(() => isPremiumTier(this._snapshot().tier));
  readonly state = computed(() => this._snapshot().state ?? null);
  readonly policy = computed(() => this._snapshot().policy ?? null);
  readonly usageToday = computed(() => this._snapshot().usageToday ?? null);

  // ── B2B organization coverage (§9.5) ────────────────────────────────────
  /** The organization grant covering this member, or null for everyone else. */
  readonly orgGrant = computed(() => this._snapshot().orgGrant ?? null);
  /**
   * THE single flag every commerce surface switches on: true → hide purchase,
   * upgrade and renewal CTAs. Read this rather than inferring from a zero price
   * — a zero cannot say who paid, and a covered member being shown an upgrade
   * CTA is being asked to buy what they already have.
   */
  readonly hidePurchase = computed(() => this._snapshot().orgGrant?.hidePurchase === true);
  /** «مُهدى من جامعة القصيم» — ready to render beside the tier row. */
  readonly orgGrantLabel = computed(() => this._snapshot().orgGrant?.label ?? null);
  /** Monthly / annual plans split out for the picker. */
  readonly monthlyPlan = computed(() => this._plans().find((p) => p.cycle === 'monthly') ?? null);
  readonly annualPlan = computed(() => this._plans().find((p) => p.cycle === 'annual') ?? null);

  // ─── per-action lifecycles (loading + error + duplicate guard) ─────────
  // Each carries the i18n key for ITS OWN failure copy, used only when the
  // backend sent no `response.message` — so "couldn't load your invoices" and
  // "couldn't cancel your subscription" never collapse into one generic line.
  readonly read = new ActionState(SubscriptionErrorKeys.read, SubscriptionOps.read);
  readonly plansLoad = new ActionState(SubscriptionErrorKeys.plans, SubscriptionOps.plans);
  readonly trial = new ActionState(SubscriptionErrorKeys.trial, SubscriptionOps.trial);
  readonly subscribe = new ActionState(SubscriptionErrorKeys.subscribe, SubscriptionOps.subscribe);
  readonly reactivateAction = new ActionState(SubscriptionErrorKeys.reactivate, SubscriptionOps.reactivate);
  readonly cancelAction = new ActionState(SubscriptionErrorKeys.cancel, SubscriptionOps.cancel);
  readonly autoRenewAction = new ActionState(SubscriptionErrorKeys.autoRenew, SubscriptionOps.autoRenew);
  readonly paymentMethodAction = new ActionState(SubscriptionErrorKeys.paymentMethod, SubscriptionOps.paymentMethod);
  readonly quoteLoad = new ActionState(SubscriptionErrorKeys.quote, SubscriptionOps.quote);
  readonly invoicesLoad = new ActionState(SubscriptionErrorKeys.invoices, SubscriptionOps.invoices);
  readonly cardsLoad = new ActionState(SubscriptionErrorKeys.cards, SubscriptionOps.cards);

  /** Concurrent `load()` callers share one request instead of stampeding the API. */
  private inflight: Promise<void> | null = null;
  /** The bearer the cached state belongs to — a different user invalidates it. */
  private ownerToken: string | null = null;

  constructor() {
    // Paint the last known tier immediately (cold start / offline) so no surface
    // flashes "free" at a paying user before the network answers.
    this._snapshot.set(this.readCache());
    this._pendingCheckout.set(this.readPendingCheckout());
  }

  // ─── reads ────────────────────────────────────────────────────────────
  /**
   * Read the snapshot once per session (per signed-in user). Idempotent: a
   * resolved read is not repeated and concurrent callers share the in-flight
   * promise, so every feature may call this freely on entry.
   */
  load(): Promise<void> {
    this.dropCacheOnUserChange();
    if (this._loaded()) return Promise.resolve();
    if (this.inflight) return this.inflight;
    this.inflight = this.runRead().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  /**
   * Force a fresh `GET /subscription` — after a checkout return, a 429 from a
   * metered feature, a manual retry, or a tab regaining focus mid-checkout.
   * Never rejects; failures leave the previous snapshot in place and are visible
   * through {@link read}.
   */
  refresh(): Promise<void> {
    this.dropCacheOnUserChange();
    if (this.inflight) return this.inflight;
    this.inflight = this.runRead().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  /** Load the public plan catalog once (idempotent; silent on failure). */
  async loadPlans(force = false): Promise<void> {
    if (!force && this._plans().length) return;
    const catalog = await this.runAction(this.plansLoad, () => firstValueFrom(this.repo.plans()));
    if (!catalog) return;
    this._plans.set(catalog.plans);
    this._freePolicy.set(catalog.freePolicy);
  }

  /** Read the Khoyek tool count once (best-effort: a failure just leaves the number out). */
  async loadToolsCount(): Promise<void> {
    if (this._toolsCount() !== null) return;
    const count = await firstValueFrom(this.repo.toolsCount()).catch(() => null);
    if (count !== null) this._toolsCount.set(count);
  }

  /** Load billing history (`GET /invoices`). */
  async loadInvoices(force = false): Promise<void> {
    if (!force && this.invoicesLoad.succeeded()) return;
    const invoices = await this.runAction(this.invoicesLoad, () => firstValueFrom(this.repo.invoices()));
    if (invoices) this._invoices.set(invoices);
  }

  /** Load saved cards (`GET /cards`). */
  async loadCards(force = false): Promise<void> {
    if (!force && this.cardsLoad.succeeded()) return;
    const cards = await this.runAction(this.cardsLoad, () => firstValueFrom(this.repo.cards()));
    if (cards) this._cards.set(cards);
  }

  // ─── mutations ────────────────────────────────────────────────────────
  /**
   * Start the one-time free 7-day trial (§5/§8.3). Rejects on failure so the
   * caller can surface it: a 422 here means the server refused the transition
   * (already premium, or the trial was consumed) and must NEVER be papered over
   * with a local trial.
   */
  async startTrial(): Promise<void> {
    const patch = await this.runAction(this.trial, () => firstValueFrom(this.repo.startTrial()));
    if (!patch) throw new Error('trial-failed');
    this.commit(mergeSnapshot(this._snapshot(), patch));
    // The trial echo is partial (no plan/usage) — re-read for the full picture.
    void this.refresh();
  }

  /**
   * Cancel → `cancelled_grace`, keeping premium for the 30-day grace (§5/§8.5).
   * The optional `reason` is the S4 exit survey; it rides on the request body
   * and is simply ignored by a backend that doesn't collect it.
   */
  async cancel(reason?: string | null): Promise<void> {
    const patch = await this.runAction(this.cancelAction, () => firstValueFrom(this.repo.cancel(reason)));
    if (!patch) throw new Error('cancel-failed');
    this.commit(mergeSnapshot(this._snapshot(), patch));
    // The cancel echo is partial (state + grace window) — re-read so the policy,
    // countdown and every premium gate follow the server, not the patch.
    void this.refresh();
  }

  /**
   * Begin a paid subscription (`POST /subscribe`) and report what to do next.
   * Does NOT navigate and does NOT touch the tier — activation belongs to the
   * gateway webhook, so the caller redirects and the return trip re-reads.
   */
  subscribeTo(options: CheckoutOptions = {}): Promise<CheckoutOutcome> {
    return this.checkout(this.subscribe, (req) => firstValueFrom(this.repo.subscribe(req)), options);
  }

  /** Resume after a cancellation (`POST /reactivate`) — same contract as {@link subscribeTo}. */
  reactivate(options: CheckoutOptions = {}): Promise<CheckoutOutcome> {
    return this.checkout(this.reactivateAction, (req) => firstValueFrom(this.repo.reactivate(req)), options);
  }

  /**
   * Route a "give me premium" CTA to the endpoint the CURRENT server state
   * allows: grace / post-cancellation must go through `/reactivate`, everything
   * else through `/subscribe` (the wrong one answers 422).
   */
  resumePremium(options: CheckoutOptions = {}): Promise<CheckoutOutcome> {
    const state = this.state();
    return state === 'cancelled_grace' || state === 'post_cancellation'
      ? this.reactivate(options)
      : this.subscribeTo(options);
  }

  /** Turn automatic renewal on/off. Rejects on failure (the switch reverts). */
  async setAutoRenew(on: boolean): Promise<void> {
    const patch = await this.runAction(this.autoRenewAction, () => firstValueFrom(this.repo.setAutoRenew(on)));
    if (!patch) throw new Error('auto-renew-failed');
    this.commit(mergeSnapshot(this._snapshot(), patch));
  }

  /**
   * «تغيير البطاقة» (SUBSCRIPTION-RECURRING R3): ask the server for the captured,
   * refunded verification charge. Does NOT navigate and does NOT touch the
   * snapshot — the caller shows the server's notice first, then hands off with
   * {@link handOffCardUpdate}; the new mandate lands only when the charge captures.
   * Rejects on failure (`403 not_premium_active`, `400` gateway page not created).
   */
  async requestCardUpdate(): Promise<CardUpdateCheckout> {
    const checkout = await this.runAction(this.paymentMethodAction, () =>
      firstValueFrom(this.repo.updatePaymentMethod(this.checkoutReturnUrl())),
    );
    if (!checkout) throw new Error('payment-method-failed');
    return checkout;
  }

  /** Record the card-update hand-off so the return trip confirms a MANDATE, not premium. */
  handOffCardUpdate(): void {
    const snapshot = this._snapshot();
    this.markCheckoutPending(snapshot.plan?.id ?? 0, snapshot.cycle ?? 'monthly', 'card_update');
  }

  /**
   * Price a plan before committing (wallet + promo). Never rejects: when the
   * quote endpoint is unavailable the summary falls back to the plain plan price
   * and {@link quote} stays null, so checkout is never blocked by it.
   */
  async requestQuote(options: CheckoutOptions = {}): Promise<SubscriptionQuote | null> {
    const cycle = options.cycle ?? this.defaultCycle();
    await this.loadPlans();
    const plan = this.resolvePlan(options.planId, cycle);
    if (!plan) {
      this._quote.set(null);
      return null;
    }
    const request = this.buildRequest(plan.id, cycle, options);
    const quoted = await this.runAction(this.quoteLoad, () => firstValueFrom(this.repo.quote(request)));
    // A quote with no subtotal (repo had no plan price to fall back on) is
    // completed from the catalog so the summary always shows a real number.
    const quote = quoted ? { ...quoted, subtotal: quoted.subtotal || plan.price } : null;
    this._quote.set(quote);
    return quote;
  }

  /** Drop a stale breakdown (e.g. when the user switches cycle or leaves checkout). */
  clearQuote(): void {
    this._quote.set(null);
  }

  // ─── checkout hand-off / return ───────────────────────────────────────
  /**
   * Record that the tab is being handed to the hosted checkout. Survives the
   * full-page navigation in storage so the return trip (or a browser Back) knows
   * to verify with the server instead of guessing.
   */
  protected markCheckoutPending(
    planId: number,
    cycle: SubscriptionCycle,
    purpose: CheckoutPurpose = 'subscribe',
  ): void {
    const pending: PendingCheckout = { planId, cycle, startedAt: this.now(), purpose };
    this._pendingCheckout.set(pending);
    this.storage.set(StorageKeys.SUBSCRIPTION_CHECKOUT, pending);
  }

  /** Forget the hand-off (resolved, abandoned, or expired). */
  clearCheckoutPending(): void {
    this._pendingCheckout.set(null);
    this.storage.remove(StorageKeys.SUBSCRIPTION_CHECKOUT);
  }

  /**
   * Resolve a checkout the user is coming back from: re-read the server and, if
   * it landed (or the marker went stale), clear it. Safe to call on every entry —
   * it resolves `null` without a fresh marker.
   *
   * What "landed" means depends on the hand-off: a `subscribe` lands when the
   * user is premium; a `card_update` (the user is ALREADY premium) lands when a
   * renewal mandate is on file. With the gateway's `chargeId` in hand, the charge
   * is verified first (`/payments/tap/verify`, the fast path — the webhook stays
   * authoritative); a card update the server reports as NOT minting a mandate
   * resolves `failed` at once instead of waiting out the backoff.
   */
  async resolveCheckoutReturn(chargeId: string | null = null): Promise<CheckoutResolution | null> {
    const pending = this._pendingCheckout();
    if (!pending) return null;
    if (!isCheckoutFresh(pending, this.now())) {
      this.clearCheckoutPending();
      return null;
    }
    const purpose = pending.purpose ?? 'subscribe';
    const verification = chargeId ? await this.verifyCharge(chargeId) : null;
    await this.refresh();

    if (purpose === 'card_update') {
      if (verification?.mandateCaptured === false) {
        this.clearCheckoutPending();
        return { purpose, status: 'failed' };
      }
      const saved = this._snapshot().mandateOnFile === true;
      if (saved) this.clearCheckoutPending();
      return { purpose, status: saved ? 'confirmed' : 'pending' };
    }

    const premium = this.isPremium();
    if (premium) this.clearCheckoutPending();
    return { purpose, status: premium ? 'confirmed' : 'pending' };
  }

  /** Wipe every cached slice (sign-out, or a different user on the same device). */
  reset(): void {
    this._snapshot.set({ ...INITIAL_SUBSCRIPTION });
    this._loaded.set(false);
    this._plans.set([]);
    this._invoices.set([]);
    this._cards.set([]);
    this._quote.set(null);
    this._toolsCount.set(null);
    this.clearCheckoutPending();
    this.ownerToken = this.session.token();
    this.inflight = null;
  }

  // ─── internals ────────────────────────────────────────────────────────
  /**
   * Run one action under its {@link ActionState} and log the OUTCOME with the
   * state that matters for support: which endpoint, whether the backend supplied
   * its own `response.message`, and what the user will therefore be shown (the
   * server's words, or this action's specific fallback key). The transport layer
   * already logged the wire detail; this line records the DECISION.
   */
  private async runAction<T>(action: ActionState, op: () => Promise<T>): Promise<T | null> {
    const result = await action.run(op);
    if (action.failed()) {
      this.logger.error(`${action.label} failed`, {
        httpStatus: action.status(),
        apiMessage: action.apiMessage(),
        showing: action.apiMessage() ? 'response.message' : action.fallbackKey,
      });
    }
    return result;
  }

  /**
   * Fast-path charge check. Never throws and never decides on its own: a failed
   * verify (older backend, network) simply leaves the `GET /subscription` re-read
   * to answer, exactly as before the endpoint existed.
   */
  private async verifyCharge(chargeId: string): Promise<ChargeVerification | null> {
    try {
      return await firstValueFrom(this.repo.verifyCharge(chargeId));
    } catch {
      return null;
    }
  }

  /** The single `GET /subscription` read. Falls back to the cache, never throws. */
  private async runRead(): Promise<void> {
    // Whose subscription this read is for. If the session changes mid-flight
    // (sign-out, or another account signing in on this tab) the answer belongs to
    // someone else and must be dropped — never shown to the person now looking.
    const startedFor = this.session.token();
    const snapshot = await this.runAction(this.read, () => firstValueFrom(this.repo.getSnapshot()));
    if (this.session.token() !== startedFor) {
      this.logger.lifecycle('Subscription read discarded — session changed mid-flight');
      return;
    }
    if (snapshot) {
      this.commit(snapshot);
      this._loaded.set(true);
      this.ownerToken = startedFor;
      return;
    }
    // Failed: keep whatever we already show (cache from the constructor, or the
    // last good read) and leave `read.failed()` for the UI to offer a retry.
    // `runAction` already logged the failure — nothing to add here.
  }

  /**
   * Shared body of `/subscribe` and `/reactivate`: resolve the plan, POST, and
   * translate the answer into a {@link CheckoutOutcome}. A `redirect` outcome
   * also records the pending-checkout marker; an `activated` one re-reads so the
   * new state is authoritative rather than assumed.
   */
  private async checkout(
    action: ActionState,
    call: (request: CheckoutRequest) => Promise<CheckoutOutcome>,
    options: CheckoutOptions,
  ): Promise<CheckoutOutcome> {
    const cycle = options.cycle ?? this.defaultCycle();
    await this.loadPlans();
    const plan = this.resolvePlan(options.planId, cycle);
    if (!plan) throw new Error('no-plan');
    const request = this.buildRequest(plan.id, cycle, options);
    const outcome = await this.runAction(action, () => call(request));
    if (!outcome) throw new Error('checkout-failed');
    if (outcome.kind === 'redirect') {
      this.markCheckoutPending(plan.id, cycle);
    } else {
      // Settled inline (wallet) or awaiting the webhook — the server decides,
      // so re-read instead of writing a tier ourselves.
      await this.refresh();
    }
    this.logger.uiEvent('Subscription checkout', { planId: plan.id, cycle, outcome: outcome.kind });
    return outcome;
  }

  /** Explicit plan id when given, else the catalog entry for the cycle. */
  private resolvePlan(planId: number | undefined, cycle: SubscriptionCycle): SubscriptionPlan | null {
    const plans = this._plans();
    if (planId != null) return plans.find((p) => p.id === planId) ?? null;
    return planForCycle(plans, cycle);
  }

  /** Build the shared subscribe/reactivate/quote request from the chosen options. */
  private buildRequest(planId: number, cycle: SubscriptionCycle, options: CheckoutOptions): CheckoutRequest {
    return {
      planId,
      cycle,
      promoCode: options.promoCode?.trim() || null,
      useWallet: options.useWallet === true,
      cardId: options.cardId ?? null,
      paymentMethod: options.paymentMethod ?? null,
      redirectUrl: this.checkoutReturnUrl(),
    };
  }

  /**
   * Where the gateway should drop the browser once the charge settles: back onto
   * the subscription surface with a marker the page reacts to. Absolute (the
   * gateway is off-origin) and browser-only.
   */
  private checkoutReturnUrl(): string | null {
    const win = this.platform.window;
    if (!win) return null;
    return `${win.location.origin}${APP_PATHS.SUBSCRIPTION}?checkout=return`;
  }

  /** The cadence to price by default — the running plan's, else monthly. */
  private defaultCycle(): SubscriptionCycle {
    return this._snapshot().cycle ?? this._snapshot().plan?.cycle ?? 'monthly';
  }

  /** Publish a snapshot AND mirror the coarse tier to storage for the next cold start. */
  private commit(snapshot: SubscriptionSnapshot): SubscriptionSnapshot {
    this._snapshot.set(snapshot);
    this.writeCache(snapshot);
    return snapshot;
  }

  private writeCache(snapshot: SubscriptionSnapshot): void {
    this.storage.set(StorageKeys.SUBSCRIPTION_TIER, snapshot.tier);
    if (snapshot.trialExpiresAt) this.storage.set(StorageKeys.SUBSCRIPTION_TRIAL_EXPIRES, snapshot.trialExpiresAt);
    else this.storage.remove(StorageKeys.SUBSCRIPTION_TRIAL_EXPIRES);
    if (snapshot.nextBillAt) this.storage.set(StorageKeys.SUBSCRIPTION_NEXT_BILL, snapshot.nextBillAt);
    else this.storage.remove(StorageKeys.SUBSCRIPTION_NEXT_BILL);
  }

  /**
   * The last known tier from storage, with the trial's own expiry applied so a
   * lapsed trial can never paint premium while the network read is in flight.
   * A cached premium whose stored period end already passed is dropped the same
   * way (a loading/offline frame must never show Premium the server may have
   * ended; a grace user simply sees free for one frame until the read lands).
   * The server remains authoritative — this only avoids a wrong first frame.
   */
  private readCache(): SubscriptionSnapshot {
    const stored = this.storage.get<SubscriptionTier>(StorageKeys.SUBSCRIPTION_TIER);
    const trialExpiresAt = this.storage.get<number>(StorageKeys.SUBSCRIPTION_TRIAL_EXPIRES) ?? 0;
    const nextBillAt = this.storage.get<number>(StorageKeys.SUBSCRIPTION_NEXT_BILL) ?? 0;
    let tier: SubscriptionTier = stored === 'trial' || stored === 'premium' ? stored : 'free';
    if (tier === 'trial' && trialExpiresAt && this.now() > trialExpiresAt) tier = 'free';
    if (tier === 'premium' && nextBillAt && this.now() > nextBillAt) tier = 'free';
    return {
      ...INITIAL_SUBSCRIPTION,
      tier,
      trialExpiresAt: tier === 'trial' ? trialExpiresAt : 0,
      nextBillAt: tier === 'premium' ? nextBillAt : 0,
      hadTrial: !!trialExpiresAt,
    };
  }

  private readPendingCheckout(): PendingCheckout | null {
    const pending = this.storage.get<PendingCheckout>(StorageKeys.SUBSCRIPTION_CHECKOUT);
    if (!pending || typeof pending.startedAt !== 'number') return null;
    if (!isCheckoutFresh(pending, this.now())) {
      this.storage.remove(StorageKeys.SUBSCRIPTION_CHECKOUT);
      return null;
    }
    return pending;
  }

  /**
   * Invalidate everything when the bearer changed (a different account signed in
   * on this tab). Pull-based rather than an `effect`, so the store has no
   * change-detection footprint and stays trivially SSR-safe.
   */
  private dropCacheOnUserChange(): void {
    const token = this.session.token();
    if (this.ownerToken !== null && token !== this.ownerToken) {
      this.logger.lifecycle('Subscription cache dropped — session changed');
      this.reset();
    }
    this.ownerToken = token;
  }

  /** `Date.now()` off the render path; 0 on the server keeps countdowns clamped. */
  private now(): number {
    return this.platform.isBrowser ? Date.now() : 0;
  }
}
